/**
 * TaskCommentBridgeService
 *
 * When a staff member replies to a task_comment message in team chat,
 * this service writes the reply back to the original comment table
 * so it's visible in the client/partner portal.
 *
 * The `from_team_chat = true` flag prevents the DB trigger from
 * creating a duplicate team_chat_messages entry (loop prevention).
 */

import { supabase } from '../supabase';
import type { TeamChatMessage } from '../stores/teamChatStore';

/**
 * Writes a team chat reply back to the original task comment table.
 * Call this after sendMessage() when replying to a task_comment message.
 */
export async function replyToTaskComment(
  parentMessage: TeamChatMessage,
  replyContent: string,
  userId: string,
  userName: string
): Promise<void> {
  const metadata = parentMessage.metadata;
  if (!metadata?.source_table || !metadata?.task_id) {
    console.warn('[TaskCommentBridge] Parent message has no metadata, skipping reply-back');
    return;
  }

  const taskId = metadata.task_id as string;
  const sourceTable = metadata.source_table as string;

  // Get organization_id from the task
  const { data: task } = await supabase
    .from('tasks')
    .select('project_id, projects:project_id(organization_id)')
    .eq('id', taskId)
    .single();

  const organizationId = (task as any)?.projects?.organization_id;
  if (!organizationId) {
    console.error('[TaskCommentBridge] Could not resolve organization_id for task', taskId);
    return;
  }

  try {
    if (sourceTable === 'client_task_comments') {
      // Reply to client comment → insert as staff reply
      const { error } = await supabase
        .from('client_task_comments')
        .insert({
          organization_id: organizationId,
          task_id: taskId,
          content: replyContent,
          author_type: 'staff',
          author_id: userId,
          author_name: userName,
          is_read: true,
          from_team_chat: true,
        });

      if (error) {
        console.error('[TaskCommentBridge] Failed to write to client_task_comments:', error);
      } else {
        console.log('[TaskCommentBridge] Reply written to client_task_comments');
      }
    } else if (sourceTable === 'task_comments') {
      // Reply to partner/internal comment → insert as staff comment
      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          content: replyContent,
          created_by: userId,
          organization_id: organizationId,
          is_public: true,
          from_team_chat: true,
        });

      if (error) {
        console.error('[TaskCommentBridge] Failed to write to task_comments:', error);
      } else {
        console.log('[TaskCommentBridge] Reply written to task_comments');
      }
    }
  } catch (err) {
    console.error('[TaskCommentBridge] Unexpected error:', err);
  }
}
